import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { MaterialTreeNode } from "../MaterialInterface.type";
import { TranslateModule } from "@ngx-translate/core";
import { MaterialTreeComponent } from "./material-tree/material-tree.component";
import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";

/**
 * Wraps a `MaterialTreeComponent` with a node search box, a select-all/expand-all toggle,
 * and top/bottom scroll buttons that appear when the tree overflows its container.
 */
@Component({
  selector: "app-material-tree-controller",
  templateUrl: "./material-tree-controller.component.html",
  styleUrls: ["./material-tree-controller.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatInputModule, FormsModule, NgClass, MaterialTreeComponent, TranslateModule]
})
export class MaterialTreeControllerComponent implements AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  @Input()
  get nodes(): MaterialTreeNode[] {
    return this._nodes;
  }
  set nodes(nodes: MaterialTreeNode[]) {
    this._nodes = nodes;
    this.originalNodes = [...this._nodes];
    this.updateSelectedNodes(true);
  }
  _nodes: MaterialTreeNode[];
  originalNodes: MaterialTreeNode[];
  expanded: boolean = false;
  searchedNode: string;
  showScroll: boolean;
  maxSroll: boolean = false;
  minScroll: boolean = true;
  @Output() selectedChange = new EventEmitter<(number | string)[]>();
  @ViewChild("matTreeContainer") matTreeContainer: ElementRef;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor() {}

  /***************************************************************************/
  // ANCHOR After view rendering
  /***************************************************************************/

  ngAfterViewInit(): void {
    this.checkScroll();
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Filter nodes
  filterTree(): void {
    if (this.searchedNode != null && this.searchedNode !== "") {
      let filteredData = this.originalNodes.filter(
        (node: MaterialTreeNode) =>
          node.text.toLowerCase().includes(this.searchedNode.toLowerCase()) ||
          node.children?.some((node: MaterialTreeNode) =>
            node.text.toLowerCase().includes(this.searchedNode.toLowerCase())
          )
      );
      this._nodes = filteredData;
    } else {
      this._nodes = [...this.originalNodes];
    }
  }

  // Update expand/collapse state of nodes
  updateExpanded(): void {
    this._nodes.map((node: MaterialTreeNode) => (node.expanded = this.expanded));
  }

  // Update scroll state
  updateScroll(): void {
    this.maxSroll =
      this.matTreeContainer?.nativeElement?.scrollTop ===
      this.matTreeContainer?.nativeElement?.scrollHeight -
        this.matTreeContainer?.nativeElement?.offsetHeight;
    this.minScroll = this.matTreeContainer?.nativeElement?.scrollTop === 0;
  }

  // Check whether a scrollbar exists
  checkScroll() {
    // Defer until the tree has laid out so scrollHeight is measurable.
    setTimeout(() => {
      this.showScroll =
        this.matTreeContainer.nativeElement.scrollHeight >
        this.matTreeContainer.nativeElement.offsetHeight;
      if (this.showScroll) {
        this.updateScroll();
      }
    }, 0);
  }

  // Update selected nodes
  updateSelectedNodes(avoidEmit?: boolean): void {
    let selectedNodes: (number | string)[] = [];
    this.originalNodes.forEach((node: MaterialTreeNode) =>
      node.children.forEach((node: MaterialTreeNode) => {
        if (node.checked) {
          selectedNodes.push(node.value);
        }
      })
    );

    if (!avoidEmit) {
      this.selectedChange.emit(selectedNodes);
    }
  }
}
